import React, { useState, useEffect } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import VisitMap from "./components/visit_map";
import styles from "./edc_grid.module.css";
import EdcGridSidebar from "./components/sidebar";
import { get, post } from "util/requests";
import SubjectQueryView from "./components/subject_query_view";
import DomainTag from "views/subjects/components/domain_tag";
import ConsentView from "./components/consent_view";
import DeviationsView from "./components/deviations_view";
import FileUploadUI from "../corelab/components/file_upload_ui";
import router from "routes";
import { useSelector } from "react-redux";
import { arrayHaveCommonItems } from "util/helpers";
import { useSearchParams, useParams, useLocation } from "react-router-dom";

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function TabPanel(props) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && <React.Fragment>{children}</React.Fragment>}
    </div>
  );
}

export default function EDCGrid(props) {
  const [search_params = [], setSearchParams] = useSearchParams();
  const { subject_id, tab_id } = useParams();
  const site_id = 1;

  const [patientStatus, setPatientStatus] = useState({
    screened_domains: [],
    randomization_domains: [],
  });
  const permissions = useSelector((state) => state.user.userData.permissions);
  const study_id = useSelector((state) => state.user.userData.active_study_id);
  const location = useLocation();

  let params = {};
  search_params.forEach((value, key) => (params[key] = value));

  useEffect(() => {
    post("/footprint", {
      path: location.pathname,
      action: "pageload",
      name: "edc_grid_base",
      study_id,
      subject_id,
    });
  }, [location.pathname]);

  useEffect(() => {
    get(`/patient_study_info/${subject_id}`)
      .then((ret) => {
        if (["no_domain_access", "no_site_access"].includes(ret.data.status)) {
          router.navigate("/subjects");
          return;
        }
        if (ret.data.status) {
          setPatientStatus(ret.data);
        }
      })
      .catch(() => router.navigate("/subjects"));
  }, [subject_id]);

  const handleChange = (event, newValue) => {
    let _new = Object.entries(tab_id_map).find((key, value) => value === newValue);
    let _location = location.pathname.split("/");
    _location = _location.slice(0, -1);
    _location.push(_new[0]);
    if (params.site) {
      let loc = `${_location.join("/")}?site=${params.site}`;
      router.navigate(loc);
    } else {
      router.navigate(_location.join("/"));
    }
  };

  let tab_id_map = { view: 0, query: 1 };
  if (arrayHaveCommonItems(permissions, ["consent_record_view", "consent_record_update"])) {
    tab_id_map["consent"] = Object.values(tab_id_map).length;
  }
  tab_id_map["deviation"] = Object.values(tab_id_map).length;
  if (arrayHaveCommonItems(permissions, ["corelab_user", "corelab_qc"])) {
    tab_id_map["corelab"] = Object.values(tab_id_map).length;
  }
  let value = tab_id_map[tab_id];

  let tabs = [
    (index) => (
      <Tab
        label="Visit Map"
        {...a11yProps(index)}
        sx={{
          "&.MuiTab-root": {},
          "&.Mui-selected": {
            borderBottom: "0px",
          },
        }}
      />
    ),
    (index) => <Tab label="Queries" {...a11yProps(index)} />,
  ];

  let panels = [
    (index) => (
      <TabPanel value={value} index={index} key={index}>
        <div className={styles.tab_panel}>
          <VisitMap subject_id={subject_id} site_id={site_id} />
        </div>
      </TabPanel>
    ),
    (index) => (
      <TabPanel value={value} index={index} key={index}>
        <div className={styles.tab_panel}>
          <SubjectQueryView subject_id={subject_id} site_id={site_id} />
        </div>
      </TabPanel>
    ),
  ];

  if (arrayHaveCommonItems(permissions, ["consent_record_view", "consent_record_update"])) {
    tabs.push((index) => <Tab label="Consent" {...a11yProps(index)} />);
    panels.push((index) => (
      <TabPanel value={value} index={index} key={index}>
        <div className={styles.tab_panel}>
          <ConsentView subject_id={subject_id} study_id={study_id} />
        </div>
      </TabPanel>
    ));
  }
  tabs.push((index) => <Tab label="Deviations" {...a11yProps(index)} />);
  panels.push((index) => (
    <TabPanel value={value} index={index} key={index}>
      <div className={styles.tab_panel}>
        <DeviationsView subject_id={subject_id} study_id={study_id} />
      </div>
    </TabPanel>
  ));
  if (arrayHaveCommonItems(permissions, ["corelab_user", "corelab_qc"])) {
    tabs.push((index) => <Tab label="Corelab" {...a11yProps(index)} />);
    panels.push((index) => (
      <TabPanel value={value} index={index} key={index}>
        <div className={styles.tab_panel}>
          <FileUploadUI subject_id={subject_id} study_id={study_id} viewMode={"file"} parent={"edc"} />
        </div>
      </TabPanel>
    ));
  }

  return (
    <div className={styles.wrapper}>
      <EdcGridSidebar subject_id={subject_id} site_id={site_id} />
      <div className={styles.tab_wrapper}>
        <div className={styles.flex}>
          <div>
            <div className={styles.subject_title}>
              <strong>{subject_id}</strong>
            </div>
            <div className={styles.site_title2}>{`${patientStatus.site_name} (${patientStatus.site_code})`}</div>
          </div>
          <div>
            <div className={styles.tiles}>
              <div className={styles.tile_label}>Randomized:</div>
              {patientStatus?.randomization_domains?.map((domain) => (
                <DomainTag text={domain} key={domain} />
              ))}
            </div>
            <div className={styles.tiles}>
              <div className={styles.tile_label}>Screened:</div>
              {patientStatus?.screened_domains?.map((domain) => (
                <DomainTag text={domain} key={domain} />
              ))}
            </div>
          </div>
        </div>
        <Tabs value={value} onChange={handleChange} aria-label="subject interface">
          {tabs.map((tab, index) => tab(index))}
        </Tabs>
        {panels.map((panel, index) => panel(index))}
      </div>
    </div>
  );
}
